import { Paper } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRecoilValue, useSetRecoilState } from "recoil";
import AdView from "../../components/ui/AdView";
import GuideHandOverlay from "../../components/ui/GuideHandOverlay";
import { Ad_RefreshKey, BoardInfoState_CommentRegKey, DialogMessageState, ImgViewer, MoveButtonState, RiaBottomState } from "../../recoil/states";
import { asyncReqBoardInfo } from "../../reducers/BoardInfoReducer";
import { asyncReqBoardJokeNextList } from "../../reducers/BoardJokeNextListReducer";
import { asyncReqCommentList, CommentListActions } from "../../reducers/CommentListReducer";
import { asyncReqCommentNewList } from "../../reducers/CommentNewListReducer";
import { LoginBtnsActions } from "../../reducers/LoginBtnsReducer";
import { TopSearchActions } from "../../reducers/TopSearchReducer";
import { AppDispatch, RootState } from "../../Store";
import CommonUtil from "../../utils/CommonUtil";
import BoardJokeNextList from "./BoardJokeNextList";
import CommentList from "./CommentList";
import CommentNewList from "./CommentNewList";
import CommentReg from "./CommentReg";
import MoveButtons from "./MoveButtons";

const Index = () => {

    const BoardInfoReducer_Result = useSelector((state: RootState) => state.BoardInfoReducer); // 게시판 표시 처리를 위한 state
    const setRiaBottomState = useSetRecoilState(RiaBottomState); // 하단 UI 랜더링 처리를 위해 사용되는 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다일얼로그 메세지 요청 정보를 갖는 state
    const setMoveButtonState = useSetRecoilState(MoveButtonState); // '순차 / 랜덤' 버튼 클릭 여부 정보를 갖는 state
    const boardInfoState_CommentRegKey = useRecoilValue(BoardInfoState_CommentRegKey); // 댓글등록 UI 랜더링 처리를 위해 사용되는 state
    const ad_RefreshKey = useRecoilValue(Ad_RefreshKey); // 광고 UI 랜더링 처리를 위해 사용되는 state
    const dispatch = useDispatch<AppDispatch>();

    const setImgViewer = useSetRecoilState(ImgViewer); // 이미지 뷰어 UI 랜더링 처리를 위해 사용되는 state

    // ====================================================================================================
    // 비디오 전체화면을 <MoveButtons /> 컴포넌트 영역을 제외한 UI 영역에 전체화면 모드 처리를 하기 위한 준비 - 시작

    const [softFs, setSoftFs] = useState(false); // 오버레이 on/off
    const flowWrapRef = useRef<HTMLDivElement | null>(null);
    // 전역으로 열고/닫는 헬퍼 노출 (외부 스크립트에서 호출)
    useEffect(() => {
        (window as any).openSoftFs = () => {
            (window as any).ScrollLocker?.lock();
            const isFullscreen = true;
            (window as any).isFullscreen = isFullscreen;
            setSoftFs(isFullscreen);
        }
        (window as any).closeSoftFs = () => {
            const isFullscreen = false;
            (window as any).isFullscreen = isFullscreen;
            setSoftFs(isFullscreen);
            (window as any).ScrollLocker?.unlock();
        }
        return () => {
            delete (window as any).openSoftFs;
            delete (window as any).closeSoftFs;
        };
    }, []);
    // 오버레이 CSS 한 번 주입
    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
            .softfs-active{
                position: fixed !important;
                left: 0; right: 0; top: 0;
                bottom: var(--softfs-bottom, 0px);
                margin: 0 !important; padding: 0 !important;
                z-index: 998; /* MoveButtons(999)보다 낮게 */
                background: #000;
                display:flex; align-items:center; justify-content:center;
                padding-bottom: env(safe-area-inset-bottom, 0px);
            }
            .softfs-active .flowplayer{
                width:100% !important; height:100% !important;
            }
            `;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);
    // 하단 고정 바 높이 변화 → 래퍼 CSS 변수(--softfs-bottom)로 반영
    useLayoutEffect(() => {
        const wrap = flowWrapRef.current;
        if (!wrap) return;

        const bar = document.getElementById("divFiexdRia");
        const update = () => {
            const visible = !!bar && getComputedStyle(bar).display !== "none";
            const h = visible ? (bar?.offsetHeight || 0) : 0;
            wrap.style.setProperty("--softfs-bottom", `${h}px`);
        };
        update();

        const ro = bar ? new ResizeObserver(update) : null;
        ro?.observe(bar!);
        window.addEventListener("resize", update);
        return () => { ro?.disconnect(); window.removeEventListener("resize", update); };
    }, []);
    // softFs 상태에 따라 래퍼에 클래스 토글
    useEffect(() => {
        const wrap = flowWrapRef.current;
        if (!wrap) return;
        wrap.classList.toggle("softfs-active", softFs);
        return () => wrap.classList.remove("softfs-active");
    }, [softFs]);
    function syncBottomOffsetVar() {
        const root = document.documentElement;
        const bar = document.getElementById('divFiexdRia'); // MoveButtons가 만든 하단 바
        const update = () => {
            const visible = !!bar && getComputedStyle(bar).display !== 'none';
            const h = visible ? (bar.offsetHeight || 0) : 0;
            // 전역 변수로 공유 (wrapper/플레이어 어디서든 사용 가능)
            root.style.setProperty('--softfs-bottom', `${h}px`);
        };
        update();

        // 변화 추적
        if (bar && 'ResizeObserver' in window) {
            const ro = new ResizeObserver(update);
            ro.observe(bar);
        }
        window.addEventListener('resize', update);
    }
    syncBottomOffsetVar();

    // 비디오 전체화면을 <MoveButtons /> 컴포넌트 영역을 제외한 UI 영역에 전체화면 모드 처리를 하기 위한 준비 - 종료
    // ====================================================================================================

    useEffect(() => {
        dispatch(TopSearchActions.setInputValSearch("")); // 상단 검색어 키워드 문자열 삭제
        setRiaBottomState(rawState => ({ ...rawState, currPathName: window.location.pathname })); // 이전/다음 손가락 버튼 숨김 요청
    }, [dispatch, setRiaBottomState]);

    // StrictMode 중복 마운트 방지용 1회 실행 가드
    const didInitOnceRef = useRef(false);
    useEffect(() => {
        if (didInitOnceRef.current) return; // 두 번째 진입 차단
        didInitOnceRef.current = true;

        // 최초 진입 시 1회만
        const view_type: string = CommonUtil.getQuerystring("view_type", "CURR");
        const board_seq: string = CommonUtil.getQuerystring("seq", "");

        let isClickBtnOrder = (view_type === "NEW"); // '순차' 버튼 표시여부
        let isClickBtnRandom = !isClickBtnOrder; // '랜덤' 버튼 표시여부

        // 새로고침 직전에 버튼 상태 가져오기
        const last_move_btn_type = sessionStorage.getItem("__last_move_btn_type__");
        if (last_move_btn_type) {
            sessionStorage.removeItem("__last_move_btn_type__");
            isClickBtnOrder = false;
            isClickBtnRandom = false;
            if (last_move_btn_type === "order") isClickBtnOrder = true;
            else if (last_move_btn_type === "random") isClickBtnRandom = true;
            else isClickBtnRandom = true;
        }

        setMoveButtonState(raw => ({ ...raw, isClickBtnOrder, isClickBtnRandom })); // 버튼 상태 업데이트

        dispatch(asyncReqBoardInfo({ view_type, board_seq }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        if (BoardInfoReducer_Result.status === "LOADING") {
            // 게시판 상세정보 > 통신 중 일 경우
        }
        else if (BoardInfoReducer_Result.status === "FAIL") {
            // 게시판 상세정보 > 통신 실패된 경우

            const message = "게시판 가져오기 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (BoardInfoReducer_Result.status === "COMPLETE") {
            // 게시판 상세정보 > 통신 성공된 경우

            if (BoardInfoReducer_Result.n4ErrorCode !== 0) {

                const message = BoardInfoReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else if (!BoardInfoReducer_Result.isSuccess) {

                const message = "조회된 게시물이 없습니다.";
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {

                // 게시판 고유번호가 달라진 경우, 웹브라우저 주소창 URL 변경
                const seq = CommonUtil.getQuerystring("seq", "");
                if (seq !== BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ) (window as any).urlChange("/board_info?seq=" + BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ);

                // 앱 내에서 참조되는 데이터 갱신
                const AppWebView = (window as any).AppWebView;
                if (typeof (AppWebView) !== "undefined") {
                    if (typeof (AppWebView.set_Ref_Data) === "function") {

                        AppWebView.set_Ref_Data(
                            BoardInfoReducer_Result.INFO.V_FIRST_YN,
                            BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ,
                            BoardInfoReducer_Result.INFO.UBM_TITLE
                        );
                    }
                }

                setHtmlMeta(); // 각종 메타 태그 생성
                initExtPlayer(); // 동영상 / gif 플레이어 초기화 실행
                getCommentNewList(); // 상단 신규 댓글 목록 가져오기
                getCommentList(); // 댓글 목록 가져오기
                getBoardJokeNextList(); // 하단 Masonry 레이아웃 목록 가져오기

                dispatch(LoginBtnsActions.setIsShowLodingBar(false)); // 로딩바 숨김 요청

                CommonUtil.scrollTo(".container", -7, 0); // 상단 메뉴 영역 안보이게 스크롤바 자동 이동
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BoardInfoReducer_Result]);

    /**
    * 각종 메타 태그 생성
    * 
    * @param
    * @returns
    */
    const setHtmlMeta = (): void => {

        $("head > meta[property=og:title]").remove();
        $("head > meta[property=og:description]").remove();
        $("head > meta[property=og:url]").remove();
        $("head > meta[property=og:image]").remove();
        $("head > link[rel=image_src]").remove();
        $("head > link[rel=canonical]").remove();
        $("head > meta[name=Robots]").remove();
        $("head > meta[name=subject]").remove();
        $("head > meta[name=description]").remove();
        $("head > meta[name=keywords]").remove();

        $("head").append("<meta property=\"og:title\" content=\"" + BoardInfoReducer_Result.INFO.UBM_TITLE + "\" />");
        $("head").append("<meta property=\"og:description\" content=\"" + BoardInfoReducer_Result.INFO.UBM_CONTENT + "\" />");
        $("head").append("<meta property=\"og:url\" content=\"" + process.env.REACT_APP_UGGIYO_DOMAIN + "/Board_Info.aspx?seq=" + BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ + "\" />");
        $("head").append("<meta property=\"og:image\" content=\"" + BoardInfoReducer_Result.INFO.V_PREVIEW_IMG_PATH + "\" />");
        $("head").append("<link rel=\"image_src\" href=\"" + BoardInfoReducer_Result.INFO.V_PREVIEW_IMG_PATH + "\" />");
        $("head").append("<link rel=\"canonical\" href=\"" + process.env.REACT_APP_UGGIYO_DOMAIN + "/Board_Info.aspx?seq=" + BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ + "\" />");
        $("head").append("<meta name=\"Robots\" content=\"INDEX, FOLLOW\" />");
        $("head").append("<meta name=\"subject\" content=\"" + BoardInfoReducer_Result.INFO.UBM_TITLE + "\" />");
        $("head").append("<meta name=\"description\" content=\"" + BoardInfoReducer_Result.INFO.UBM_TITLE + "\" />");
        $("head").append("<meta name=\"keywords\" content=\"우끼요,유머,유머앱,유머어플,개그,개그앱,개그어플,웃긴사진,웃긴글,웃긴사연,웃긴어플,웃긴앱,재밌는어플,재밌는앱,재밌는사진,재밌는글,재밌는사연," + BoardInfoReducer_Result.INFO.UBM_TITLE + "\" />");
    }

    // ---- 포스터 후보 중 실제로 로드 가능한 첫 URL을 고르는 헬퍼 ----
    function probeImage(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            const img = new Image();
            const done = (ok: boolean) => {
                img.onload = img.onerror = null as any;
                resolve(ok);
            };
            img.onload = () => done(true);
            img.onerror = () => done(false);
            img.decoding = "async";
            img.loading = "eager";
            img.src = url;
        });
    }

    async function pickBestPoster(previewJpg: string): Promise<string> {
        const avif = previewJpg.replace(/\.jpg$/i, ".avif");
        const webp = previewJpg.replace(/\.jpg$/i, ".webp");
        for (const u of [avif, webp, previewJpg]) {
            if (await probeImage(u)) return u;
        }
        return previewJpg; // 최후 폴백
    }

    /**
    * 동영상 / gif 플레이어 초기화 실행
    * 
    * @param
    * @returns
    */
    const initExtPlayer = (): void => {

        document.querySelector(".jqGifPreview")?.remove(); // gif 플레이어는 스크립트가 새롭게 그려주는 UI라서 리액트가 다시 렌더링을 하더라도 기존 UI가 그대로 남게되고 하단에 또 다시 UI를 그려지게 됨.. 기존 UI는 제거하고 시작!

        if (BoardInfoReducer_Result.INFO.UBM_IMG_PATH.endsWith(".mp4")) {
            // ▼ 추가: 포스터 후보 만들기 (서버에서 내려오는 PREVIEW.jpg 기준)
            const basePreviewJpg = BoardInfoReducer_Result.INFO.V_PREVIEW_IMG_PATH
                .replace("_WATERMARK.jpg", "_PREVIEW.jpg");

            // 비동기로 최적 포스터 고른 뒤 외부 스크립트 호출
            (async () => {
                const bestPoster = await pickBestPoster(basePreviewJpg);

                CommonUtil.externalScriptExec(
                    "?req=flowplayer"
                    + "&content_url=" + encodeURIComponent(BoardInfoReducer_Result.INFO.UBM_IMG_PATH)
                    + "&preview_url=" + encodeURIComponent(bestPoster)
                    + "&title=" + encodeURIComponent(BoardInfoReducer_Result.INFO.V_FILE_SIZE + " | " + BoardInfoReducer_Result.INFO.UBM_TITLE)
                );
            })();
        }
        else if (BoardInfoReducer_Result.INFO.UBM_IMG_PATH.endsWith(".gif")) {
            CommonUtil.externalScriptExec(
                "?req=gifplayer"
            );
        }
    }

    /**
    * 상단 신규 댓글 목록 가져오기
    * 
    * @param
    * @returns
    */
    const getCommentNewList = (): void => {

        const board_seq: string = BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ; // '게시판 고유번호' 파라메터 가져오기

        dispatch(asyncReqCommentNewList({ board_seq, show_dim: false })); // 상단 신규 댓글 목록 가져오기 (로딩바 표시되지 않도록 'show_dim' 값을 'false'로 세팅)
    }

    /**
    * 댓글 목록 가져오기
    * 
    * @param
    * @returns
    */
    const getCommentList = (): void => {

        const board_seq: string = BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ; // '게시판 고유번호' 파라메터 가져오기
        const comment_seq = CommonUtil.getQuerystring("c_seq", "");
        const page_size: number = parseInt(process.env.REACT_APP_COMMENT_PAGE_SIZE as string, 10);
        const curr_page: number = Math.floor((BoardInfoReducer_Result.INFO.V_COMMENT_TOTAL_COUNT / page_size) + ((BoardInfoReducer_Result.INFO.V_COMMENT_TOTAL_COUNT % page_size === 0) ? 0 : 1)); // 마지막 페이지 번호 추출

        dispatch(asyncReqCommentList({ page_size, curr_page, board_seq, show_dim: (comment_seq ? true : false) })); // 댓글 목록 가져오기 (최초 진입일 경우에는 로딩바 표시되지 않도록 'show_dim' 값을 'false'로 세팅)
        dispatch(CommentListActions.setCurrPage(curr_page)); // 현재 페이지 번호 저장
    }

    /**
    * 하단 Masonry 레이아웃 목록 가져오기
    * 
    * @param
    * @returns
    */
    const getBoardJokeNextList = (): void => {

        // 모바일 호출일 경우, 하단 웃긴사진 목록 숨김
        //if (CommonUtil.isMobileOs()) return;
        return; // 성능 문제로 인해 표시 안되도록 처리

        const board_seq: string = BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ; // '게시판 고유번호' 파라메터 가져오기
        const page_size: number = parseInt(process.env.REACT_APP_BOARD_JOKE_NEXT_PAGE_SIZE as string, 10);
        const curr_page: number = 1;

        dispatch(asyncReqBoardJokeNextList({ page_size, curr_page, board_seq, show_dim: false })); // 하단 Masonry 레이아웃 목록 가져오기 (로딩바 표시되지 않도록 'show_dim' 값을 'false'로 세팅)
    }

    const avifRef = useRef<HTMLSourceElement>(null);
    const webpRef = useRef<HTMLSourceElement>(null);

    /**
    * 게시물 이미지 Load 성공 이벤트
    * 
    * @param e 이벤트 객체
    * @returns 
    */
    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const chosenUrl = img.currentSrc || img.src; // 실제 선택된 URL
        setImgViewer(v => ({ ...v, src: chosenUrl })); // 다음 오픈 대비
        $(img).closest('.figure').show();
    };

    /**
    * 게시물 이미지 Load 실패 이벤트
    * 
    * @param e 이벤트 객체
    * @returns 
    */
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const url = img.currentSrc || img.src;

        // 파일 부재(404 등) 시 클라이언트 폴백 체인: avif -> webp -> jpg
        if (url.endsWith('.avif') && avifRef.current) {
            avifRef.current.remove(); // avif 후보 제거
            const next = BoardInfoReducer_Result.INFO.UBM_IMG_PATH.replace('.jpg', '.webp');
            img.src = next;
            setImgViewer(v => ({ ...v, src: next }));
        } else if (url.endsWith('.webp') && webpRef.current) {
            webpRef.current.remove(); // webp 후보 제거
            const next = BoardInfoReducer_Result.INFO.UBM_IMG_PATH;
            img.src = next;
            setImgViewer(v => ({ ...v, src: next }));
        } else {
            // jpg도 실패하면 마지막 처리(에러 이미지 표시 등)
            $(img).closest('.figure').show();
        }
    };

    return (
        <>
            {
                navigator.userAgent.indexOf(";UGGIYO_APP;") === -1 && <GuideHandOverlay
                    onceKey="guide.hand.shown" // 한 번만 보기
                // open={true} // 외부에서 강제 표시/숨김 제어하려면 사용
                // message={<>하단의 이동 버튼을 사용해 빠르게 탐색해 보세요.</>}
                // frameUrls={[
                // "/assets/guide/hand_01.png",
                // "/assets/guide/hand_02.png",
                // "/assets/guide/hand_03.png",
                // "/assets/guide/hand_02.png",
                // ]}
                />
            }
            <div className="container" style={{ width: "100%" }}>

                <article className="bo_v" style={{ margin: "0px" }}>

                    <CommentNewList />

                    <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_1", slot: "9837644925", height: CommonUtil.isMobileOs() ? 50 : 100 }}
                        kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_1", unit: "DAN-skjyt0f45mwk", width: 320, height: 50 }}
                        marginStyle="10px 0 0"
                        refreshKey={ad_RefreshKey}
                    />

                    <header>
                        <h2 className="bo_v_title">
                            <Paper component="span" elevation={0} className="bo_v_tit" sx={{ fontSize: "1.5em !important" }}>
                                {BoardInfoReducer_Result.INFO.UBM_TITLE}
                            </Paper>
                        </h2>
                    </header>

                    <section className="bo_v_info">
                        <div className="bo_v_info_l">
                            <strong>
                                <span className="sv_member">
                                    {
                                        BoardInfoReducer_Result.INFO.UBM_REG_MEM_UDID === "admin" ?
                                            <><i className={BoardInfoReducer_Result.INFO.V_CAT_CLASS_NM} aria-hidden="true"></i> {BoardInfoReducer_Result.INFO.V_CAT_NM}</>
                                            : <>{BoardInfoReducer_Result.INFO.UBM_REG_NICKNAME}</>
                                    }
                                </span>
                            </strong>
                        </div>
                        <div className="bo_v_info_r">
                            <strong><i className="fa fa-users" aria-hidden="true"></i> {BoardInfoReducer_Result.INFO.UBM_HIT_COUNT}</strong>
                            <strong><i className="fa fa-comments" aria-hidden="true"></i> {BoardInfoReducer_Result.INFO.V_COMMENT_TOTAL_COUNT}</strong>
                            <strong className="if_date"><i className="fa fa-clock-o" aria-hidden="true"></i> {BoardInfoReducer_Result.INFO.UBM_REG_DT}</strong>
                        </div>
                    </section>

                    <section className="bo_v_atc">

                        <div id="divContentFileRoot" className="bo_v_con" style={{ margin: "3px 0 0 0" }}>
                            {
                                BoardInfoReducer_Result.INFO.UBM_IMG_PATH.endsWith(".mp4") ?
                                    <>
                                        <img src="/Images/icon_uggiyo_url.png" alt="" style={{ float: "left", marginBottom: "2px", zIndex: 99 }} />
                                        <div id="flowWrap" ref={flowWrapRef} style={{ position: "relative", width: "100%" }}>
                                            <div className="flowplayer" data-swf="flowplayer.swf" data-ratio="0.4167" key={BoardInfoReducer_Result.INFO.UBM_BOARD_SEQ}></div>
                                        </div>
                                    </>
                                    : ""
                            }
                            {
                                (
                                    BoardInfoReducer_Result.status === "COMPLETE" &&
                                    BoardInfoReducer_Result.INFO?.UBM_IMG_PATH !== ""
                                )
                                &&
                                <>
                                    <div className="figure" style={{ paddingTop: 17, display: "none" }}>
                                        <img src="/Images/icon_uggiyo_url.png" alt="" className="figure__logo" />
                                        {
                                            BoardInfoReducer_Result.INFO.UBM_IMG_PATH.endsWith(".gif") ?
                                                <img className="figure__image"
                                                    src={BoardInfoReducer_Result.INFO.UBM_IMG_PATH.replace(".gif", "_PREVIEW.jpg")}
                                                    data-gif={BoardInfoReducer_Result.INFO.UBM_IMG_PATH}
                                                    id="imgGifPlayer"
                                                    alt=""
                                                    style={{ minWidth: "200px", border: "1px solid #000000" }}
                                                    onLoad={handleImageLoad}
                                                />
                                                : ""
                                        }
                                        {
                                            BoardInfoReducer_Result.INFO.UBM_IMG_PATH.endsWith(".jpg") ?
                                                <picture>
                                                    <source
                                                        ref={avifRef}
                                                        type="image/avif"
                                                        srcSet={BoardInfoReducer_Result.INFO.UBM_IMG_PATH.replace(".jpg", ".avif")}
                                                    />
                                                    <source
                                                        ref={webpRef}
                                                        type="image/webp"
                                                        srcSet={BoardInfoReducer_Result.INFO.UBM_IMG_PATH.replace(".jpg", ".webp")}
                                                    />
                                                    <img
                                                        className="figure__image"
                                                        src={BoardInfoReducer_Result.INFO.UBM_IMG_PATH}
                                                        alt=""
                                                        style={{ minWidth: "200px", border: "1px solid #000000" }}
                                                        onClick={(window as any).handleImageClick}
                                                        onLoad={handleImageLoad}  // ← avif/webp/jpg 모두 여기로 들어옴
                                                        onError={handleImageError} // ← 파일 부재 시 폴백
                                                    />
                                                </picture>
                                                : ""
                                        }
                                    </div>
                                    <input type="hidden" id="hf_Gif_Size" value={BoardInfoReducer_Result.INFO.V_FILE_SIZE} />
                                </>
                            }
                        </div>

                        <pre>{BoardInfoReducer_Result.INFO.UBM_CONTENT}</pre>
                        <div style={{ width: "100%", height: "20px" }}>&nbsp;</div>

                        <Paper elevation={0} sx={{ display: { lg: "none", xs: "block" } }}>
                            <div className="sns_share_pos_content" style={{ width: "100%" }}>
                                <div className="print-hide view-icon view-padding" style={{ textAlign: "left" }}>
                                    <a href="#none" title="엑스(구 트위터)" onClick={e => CommonUtil.handlerSnsShare(e, "twitter")}><img src="/images/btn_sns_twitter.png" width="35" alt="" /></a>&nbsp;
                                    <a href="#none" title="페이스북" onClick={e => CommonUtil.handlerSnsShare(e, "facebook")}><img src="/images/btn_sns_facebook.png" width="35" alt="" /></a>&nbsp;
                                    <a href="#none" title="네이버밴드" onClick={e => CommonUtil.handlerSnsShare(e, "band")}><img src="/images/btn_sns_naverband.png" width="35" alt="" /></a>&nbsp;
                                    <a href="#none" title="네이버블로그" onClick={e => CommonUtil.handlerSnsShare(e, "naverblog")}><img src="/images/btn_sns_naverblog.png" width="35" alt="" /></a>&nbsp;
                                    <a href="#none" title="핀터레스트" onClick={e => CommonUtil.handlerSnsShare(e, "pinterest")}><img src="/images/btn_sns_pinterest.png" width="35" alt="" /></a>&nbsp;
                                    {/* 공유하기 기능 종료 됨..
                                        <a href="#none" title="카카오스토리" onClick={e => CommonUtil.handlerSnsShare(e, "kakaostory")}><img src="/images/btn_sns_kakaostory.png" width="35" alt="" /></a>&nbsp; */}
                                    <a href="#none" title="카카오톡" onClick={e => CommonUtil.handlerSnsShare(e, "kakaotalk")}><img src="/images/btn_sns_kakaotalk.png" width="35" alt="" /></a>&nbsp;
                                    <a href="#none" title="텀블러" onClick={e => CommonUtil.handlerSnsShare(e, "tumblr")}><img src="/images/btn_sns_tumblr.png" width="35" alt="" /></a>
                                    <div className="clearfix"></div>
                                </div>
                            </div>
                        </Paper>
                    </section>

                    <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_2", slot: "9183038803", ...(CommonUtil.isMobileOs() ? { height: 200 } : { responsiveYn: "Y" }) }}
                        kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_2", unit: "DAN-1iyq1vrmz2c6s", width: 300, height: 250 }}
                        marginStyle="10px 0 0"
                        refreshKey={ad_RefreshKey}
                    />

                    <nav className="bo_cate" id="navCommentListRoot" style={{ width: "100%" }}>
                        <ul className="bo_cate_ul">
                            <li>
                                <span className="cmt_btn">댓글목록</span>
                            </li>
                        </ul>
                    </nav>
                    <section className="bo_vc" style={{ marginBottom: 10 }}>
                        <CommentList />

                        {
                            Math.round(Math.random()) === 0 ?
                                <a href="https://smartstore.naver.com/must-this" target="_blank" rel="noreferrer">
                                    <img src="/Images/banner_must_this.png" alt="" style={{ width: "100%" }} />
                                </a> :
                                <a href="https://play.google.com/store/apps/details?id=kr.co.moneymoa_20200803" target="_blank" rel="noreferrer">
                                    <img src="/Images/banner_manimoa.png" alt="" style={{ width: "100%" }} />
                                </a>
                        }

                        <nav className="bo_cate" id="navCommentRegRoot" style={{ width: "100%" }}>
                            <ul className="bo_cate_ul">
                                <li>
                                    <span className="cmt_btn">댓글등록</span>
                                </li>
                            </ul>
                        </nav>
                        <CommentReg key={boardInfoState_CommentRegKey} />

                    </section>

                </article>
            </div>
            <MoveButtons />
            {/* <!-- 모바일 호출일 경우, 하단 웃긴사진 목록 숨김 --> */}
            {
                true ?//CommonUtil.isMobileOs() ? // 성능 문제로 인해 표시 안되도록 처리
                    <div style={{ height: "200px" }}>&nbsp;</div> // 앱 내에서 댓글 작성 시, 관련 UI를 키패드가 가려버림.. 하단에 여유공간 확보!
                    : <BoardJokeNextList mode="AJS" />
            }
        </>
    )
}

export default Index;
